import { CircularProgress, TextLink } from '@civilgrid/ui/components';
import { type FeatureBillingTier, isBillingTierEnabled } from '@civilgrid/utils/src/common/featureAccess';
import Dinero from 'dinero.js';
import type React from 'react';
import { type ReactNode, useMemo } from 'react';
import type { AsyncState } from 'react-use/lib/useAsyncFn';
import { useSnapshot } from 'valtio';
import type { GetCostOutput } from '~/common/billingUtils';
import { useExportLabels } from '~/hooks/useExportTerminologyEnabled';
import rootStore from '~/store';

interface Section {
  id: FeatureBillingTier;
  label: string;
  cost?: string;
  description: ReactNode;
  includesText: string;
  lineItems: Array<string | React.ReactNode>;
}

interface Props {
  projectCost?: AsyncState<GetCostOutput>;
}

export default function ProjectPricingTable({ projectCost }: Props) {
  const { currentOrganization } = useSnapshot(rootStore) as typeof rootStore;
  const labels = useExportLabels();
  const sections: Section[] = [
    {
      id: 'draft',
      label: 'Draft',
      cost: projectCost ? 'Free' : undefined,
      description: 'Data availability summary and preliminary scoping',
      includesText: 'Includes',
      lineItems: [
        'Unlimited viewing of live utility map',
        'Unlimited exploration of historical site datasets',
        'Unlimited edits to project boundaries',
        'Share with your project team',
        'Upload anything to Project Files',
        <span key="civildraw">
          Use <span className="font-semibold">CivilDraw</span> to quickly create and share maps and plans, powered by
          CivilGrid data
        </span>,
      ],
    },
    {
      id: 'proposal',
      label: 'Project Lite',
      cost: projectCost
        ? Dinero({
            amount: projectCost.value!.proposal.totalCost,
          }).toFormat()
        : undefined,
      description: 'Complete utility repository, custom exhibits, and report generation',
      includesText: 'Includes everything in Draft, plus',
      lineItems: [
        <span key="proposal-generator">
          One-click proposal generator.{' '}
          <TextLink
            href="https://civilgrid.help.usepylon.com/articles/5843406317-creating-a-proposal-packet"
            target="_blank"
            className="underline"
          >
            View sample
          </TextLink>
        </span>,
        'List of utility providers',
        'Utility line attributes',
        'Automated figures - vicinity and subarea maps',
        'Export utilities report to Word',
        <span key="field-notes">
          Capture field notes and photos using <span className="font-semibold">CivilGrid Mobile</span>{' '}
        </span>,
        'Use mobile offline utility maps',
      ],
    },
    {
      id: 'project',
      label: 'Project',
      cost: projectCost ? Dinero({ amount: projectCost.value!.project.totalCost }).toFormat() : undefined,
      description: 'Thorough QL-D utility research, preliminary CAD basemap, and site diligence',
      includesText: 'Includes everything in Project Lite, plus',
      lineItems: [
        labels.pricingFeature,
        'Access full utility layer attributes',
        <span key="civilsearch">
          Request additional maps and as-builts with <span className="font-semibold">CivilSearch</span>
          <sup>*</sup>
        </span>,
        <span key="intelligence">
          Identify potential conflicts and permit triggers with{' '}
          <span className="font-semibold">CivilGrid Intelligence</span>
        </span>,
        'Order site reports - land rights, geotech, environmental, etc.**',
        'View Zoning data***',
        'Mobile potholing app and report exports',
        'Custom mobile field forms***',
      ],
    },
    {
      id: 'projectPlus',
      label: 'Project Plus',
      cost: projectCost
        ? Dinero({
            amount: projectCost.value!.projectPlus.totalCost,
          }).toFormat()
        : undefined,
      description: 'Digitization of supplemental maps and as-builts',
      includesText: 'Includes everything in Project, plus',
      lineItems: [
        <span key="digitization">
          Unlimited digitization of PDF maps and as-builts.{' '}
          <TextLink
            href="https://civilgrid.help.usepylon.com/articles/2688571974-digitization"
            target="_blank"
            className="underline"
          >
            Learn more
          </TextLink>
        </span>,
        'View CivilGrid digitized maps and as-builts in the live map',
        'Export digital data to CAD and GIS',
      ],
    },
  ];

  // Hide billing tiers the org has disabled (enabledBillingTiers override).
  const enabledSections = sections.filter((s) => isBillingTierEnabled(s.id, currentOrganization?.subscription));

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const sectionEls = useMemo(() => {
    if (projectCost?.loading) {
      return <CircularProgress indeterminate />;
    }
    const els = enabledSections.map(({ id, label, cost, description, includesText, lineItems }) => {
      const lineItemEls = lineItems.map((item, lineIndex) => (
        <li
          // biome-ignore lint/suspicious/noArrayIndexKey: legacy
          key={`${id}-line-${lineIndex}`}
        >
          ✅ {item}
        </li>
      ));

      return (
        <div className="border p-3" key={id}>
          <div className="my-4 text-center">
            <div className="text-lg font-semibold">{label}</div>
            <div className="text-lg font-bold">{cost}</div>
            <div>{description}</div>
          </div>
          <div className="my-1">{includesText}</div>
          <ul>{lineItemEls}</ul>
        </div>
      );
    });
    return <div className="grid grid-cols-2 gap-2">{els}</div>;
    // `labels.pricingFeature` is baked into a line item, and flags resolve after first paint,
    // so without it the table keeps the pre-flag wording for as long as it stays mounted.
  }, [projectCost, currentOrganization, labels.pricingFeature]);

  return (
    <div>
      {sectionEls}
      <div className="mt-4 text-2xs">
        <div>
          <sup>*</sup> CivilSearch service is included in the project cost. Any additional records research fees imposed
          by agencies are passed through.
        </div>
        <div>
          <sup>**</sup> Both free and paid reports available. Additional fees per report may apply for paid reports
          (passed through from the provider).
        </div>
        <div>
          <sup>***</sup> Additional fees may apply. Contact{' '}
          <a
            href="mailto:support@civilgrid.com"
            rel="noreferrer noopener"
            className="text-blue-50 underline hover:text-blue-60"
          >
            support@civilgrid.com
          </a>{' '}
          for pricing.
        </div>
      </div>
    </div>
  );
}
